import { useState, useEffect, useCallback } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { debounce } from 'lodash';
import SessionService from "../services/SessionService";
import { QuoteBasicDetail } from "../models/Quotes/QuoteBasicDetail";
import { useSearchParams } from "react-router-dom";

interface ProductSearchAutoCompleteProps {
  selectedItem: (productBasicDetail: QuoteBasicDetail | null) => void;
  searchTerm: string;
  setSearchTerm: (newValue: string) => void;
};

const ProductSearchAutoComplete = (props: ProductSearchAutoCompleteProps) => {
  const { selectedItem, searchTerm, setSearchTerm } = props;
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('productId');

  const [searchResult, setSearchResult] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSelection, setIsSelection] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<QuoteBasicDetail>(null);

  const sessionService = SessionService.getInstance();

  const setSearchItemAndDetails = (productDetails) => {
    const selectedValue = String(productDetails?.id ?? '');
    setSelectedId(selectedValue);
    setSearchTerm(selectedValue);
    setIsSelection(true); // Mark as dropdown selection
    selectedItem(productDetails);
    setSelectedProduct(productDetails);
  };

  useEffect(() => {
    setTimeout(async () => {
      if(productId){
        await fetchResults(`${productId}`, true, true);
      }
    }, 500)
  }, [])

  const fetchResults = async (term: string, isSelection: boolean = false, initialFetch: boolean = false) => {
    if (!`${term}`.trim()) {
      setSearchResult([]);
      setLoading(false);
      return;
    }

    const sessionInfo = sessionService.getSessionToken();
    const queryParam = isSelection ? `id=${term}` : `searchVal=${encodeURIComponent(term?.trim())}`;
    const apiUrl = `${process.env.REACT_APP_API_URL}/Quote/product-search?${queryParam}`;

    try {
      setLoading(true);
      // Simulating an API call
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + sessionInfo,
        }
      });
      const searchResponse = await response.json();
      if (searchResponse?.isSuccess) {
        setSearchResult(searchResponse?.data);
        if(initialFetch) {
          setSearchItemAndDetails(searchResponse?.data?.[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching options:", error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchResults = useCallback(
    debounce((term) => fetchResults(term, false), 400),
    []
  );

  useEffect(() => {
    if (isSelection && selectedId) {
      // If selecting from dropdown, call API with id
      fetchResults(selectedId, true);
    } else {
      // If typing, use debounced function
      debouncedFetchResults(searchTerm);
    }

    return () => {
      debouncedFetchResults.cancel();
    };
  }, [searchTerm, selectedId, isSelection, debouncedFetchResults]);

  return (
    <Autocomplete
      size="small"
      style={{ width: '400px' }}
      getOptionLabel={(optionList) => {
        if (!optionList) return ''; // Handle null or undefined options gracefully

        const id = optionList.id != null ? `${optionList.id}` : '';
        const stockcode = optionList.stockcode ? `${optionList.stockcode}` : '';
        const shortDesc = optionList.shortDesc ? `${optionList.shortDesc}` : '';
        const baseCode = optionList.baseCode ? `${optionList.baseCode}` : '';

        // Combine all non-empty parts with a separator
        return [id, stockcode, baseCode, shortDesc].filter(Boolean).join(' - ');
      }}
      value={selectedProduct ? selectedProduct : null}
      options={searchResult || []}
      renderInput={(params) => <TextField {...params} title={params.inputProps?.value as string} />}
      onInputChange={(event, newInputValue, reason) => {
        //if reason is input then new value else null
        if (reason === 'input') {
          setSearchTerm(String(newInputValue ?? ''));
          setIsSelection(false); // Mark as search input change
        }
      }}
      onBlur={() => {
        fetchResults(`${selectedProduct?.id}`, true);
      }}
      onChange={(event, newValue) => {
        const selectedValue = String(newValue?.id ?? '');
        setSelectedId(selectedValue);
        setSearchTerm(selectedValue);
        setIsSelection(true); // Mark as dropdown selection
        selectedItem(newValue);
        setSelectedProduct(newValue);
      }}
      filterOptions={(options, { inputValue }) => {
        // Custom filtering logic
        const normalizedInput = inputValue.toLowerCase();
        return options.filter((option) => {
          // Check id (convert to string), stockcode, and shortDesc
          return (
            String(option.id).toLowerCase().includes(normalizedInput) ||
            (option.stockcode && option.stockcode.toLowerCase().includes(normalizedInput)) ||
            (option.baseCode && option.baseCode.toLowerCase().includes(normalizedInput)) ||
            (option.shortDesc && option.shortDesc.toLowerCase().includes(normalizedInput))
          );
        });
      }}
      renderOption={(props, option) => {
        const optionText = `${option.id || ''}${option.stockcode ? ` - ${option.stockcode}` : ''}${option.baseCode ? ` - ${option.baseCode}` : ''}${option.shortDesc ? ` - ${option.shortDesc}` : ''}`;
        return (
          <li {...props} title={optionText}>
            {optionText}
          </li>
        );
      }}
      //disable clearable because when clear null it would error
      disableClearable={true}
      disabled={!!productId}
      loading={loading}
    />
  );

};

export default ProductSearchAutoComplete;